import { useState, useRef, useCallback, useEffect } from 'react';

interface Position {
  side: 'left' | 'right';
  top: number; // px from top, -1 = auto (bottom)
}

interface DragPos {
  x: number;
  y: number;
}

interface UseDraggableEdgeOptions {
  storageKey: string;
  defaultPosition?: Position;
  buttonSize?: number;
  margin?: number;
}

interface UseDraggableEdgeReturn {
  style: React.CSSProperties;
  isDragging: boolean;
  wasDragged: boolean;
  handlePointerDown: (e: React.PointerEvent) => void;
  menuPosition: { horizontal: 'left' | 'right'; vertical: 'top' | 'bottom' };
}

export function useDraggableEdge({
  storageKey,
  defaultPosition = { side: 'right', top: -1 },
  buttonSize = 48,
  margin = 24,
}: UseDraggableEdgeOptions): UseDraggableEdgeReturn {
  const [position, setPosition] = useState<Position>(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) return JSON.parse(stored);
    } catch {}
    return defaultPosition;
  });

  const [isDragging, setIsDragging] = useState(false);
  const [dragPos, setDragPos] = useState<DragPos | null>(null);
  const [wasDragged, setWasDragged] = useState(false);
  const dragStartRef = useRef<{ px: number; py: number; startX: number; startY: number } | null>(null);
  const hasMoved = useRef(false);

  const resolveTop = useCallback((pos: Position) => {
    if (typeof window === 'undefined') return 0;
    if (pos.top < 0) return window.innerHeight - buttonSize - margin;
    return Math.max(margin, Math.min(pos.top, window.innerHeight - buttonSize - margin));
  }, [buttonSize, margin]);

  const resolveLeft = useCallback((pos: Position) => {
    if (typeof window === 'undefined') return 0;
    return pos.side === 'left' ? margin : window.innerWidth - buttonSize - margin;
  }, [buttonSize, margin]);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (e.button !== 0) return;
    e.preventDefault();

    const startX = resolveLeft(position);
    const startY = resolveTop(position);

    dragStartRef.current = { px: e.clientX, py: e.clientY, startX, startY };
    hasMoved.current = false;
    setWasDragged(false);

    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, [position, resolveLeft, resolveTop]);

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      if (!dragStartRef.current) return;

      const dx = e.clientX - dragStartRef.current.px;
      const dy = e.clientY - dragStartRef.current.py;

      if (!hasMoved.current && Math.abs(dx) < 5 && Math.abs(dy) < 5) return;

      if (!hasMoved.current) {
        hasMoved.current = true;
        setIsDragging(true);
      }

      const x = Math.max(0, Math.min(dragStartRef.current.startX + dx, window.innerWidth - buttonSize));
      const y = Math.max(margin, Math.min(dragStartRef.current.startY + dy, window.innerHeight - buttonSize - margin));
      setDragPos({ x, y });
    };

    const onUp = () => {
      if (!dragStartRef.current) return;

      if (hasMoved.current) {
        setWasDragged(true);
        // Snap to nearest horizontal edge
        setDragPos((dp) => {
          if (!dp) return null;
          const centerX = dp.x + buttonSize / 2;
          const midX = window.innerWidth / 2;
          const newSide: 'left' | 'right' = centerX < midX ? 'left' : 'right';
          const clampedTop = Math.max(margin, Math.min(dp.y, window.innerHeight - buttonSize - margin));
          const newPos: Position = { side: newSide, top: clampedTop };
          setPosition(newPos);
          localStorage.setItem(storageKey, JSON.stringify(newPos));
          return null; // Clear drag position, go back to snapped position
        });
      }

      dragStartRef.current = null;
      hasMoved.current = false;
      setIsDragging(false);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [buttonSize, margin, storageKey]);

  // Recalculate on window resize
  useEffect(() => {
    const onResize = () => {
      setPosition((prev) => ({
        ...prev,
        top: prev.top < 0 ? -1 : Math.max(margin, Math.min(prev.top, window.innerHeight - buttonSize - margin)),
      }));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [buttonSize, margin]);

  // Compute style: during drag use absolute x/y, otherwise use side-snapped position
  const style: React.CSSProperties = dragPos
    ? {
        position: 'fixed' as const,
        left: dragPos.x,
        top: dragPos.y,
        right: undefined,
        zIndex: 55,
        transition: 'none',
        touchAction: 'none',
        userSelect: 'none',
      }
    : {
        position: 'fixed' as const,
        top: resolveTop(position),
        left: position.side === 'left' ? margin : undefined,
        right: position.side === 'right' ? margin : undefined,
        zIndex: 55,
        transition: 'all 0.3s ease',
        touchAction: 'none',
        userSelect: 'none',
      };

  const currentTop = dragPos ? dragPos.y : resolveTop(position);
  const currentSide = dragPos
    ? ((dragPos.x + buttonSize / 2) < window.innerWidth / 2 ? 'left' : 'right')
    : position.side;

  const menuPosition = {
    horizontal: (currentSide === 'left' ? 'right' : 'left') as 'left' | 'right',
    vertical: (currentTop > window.innerHeight / 2 ? 'top' : 'bottom') as 'top' | 'bottom',
  };

  return { style, isDragging, wasDragged, handlePointerDown, menuPosition };
}
